/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  Animated,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Image,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
  Linking,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
  Feather,
  AntDesign,
  Entypo,
  FontAwesome,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as Calendar from 'expo-calendar';
import * as Notifications from 'expo-notifications';
import DateTimePicker from '@react-native-community/datetimepicker';

const { width, height } = Dimensions.get('window');

// Configure notifications
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

// Add missing formatDate function
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const AE_API_BASE = 'https://api.jobeng.ai';
const AE_API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const aeStr = (v) => String(v ?? '').trim();
const aeArr = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch {}
    return v.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// Map a DB row from /api/data/experts to the expert card shape.
const aeMapExpert = (row, idx) => {
  const palette = ['#6366f1', '#FF6B35', '#19abb5', '#8b5cf6', '#10b981', '#ec4899'];
  return {
    id: aeStr(row.id ?? row.expert_id) || `EXP${idx + 1}`,
    name: aeStr(row.name) || aeStr(row.full_name) || 'Expert',
    title: aeStr(row.title) || aeStr(row.role) || '',
    company: aeStr(row.company) || '',
    experience: aeStr(row.experience) || '',
    specialization: aeStr(row.specialization) || aeStr(row.category) || 'General',
    category: aeStr(row.category) || 'all',
    rating: Number(row.rating ?? 0) || 0,
    reviews: Number(row.reviews ?? row.review_count ?? 0) || 0,
    pricePerHour: Number(row.price_per_hour ?? row.pricePerHour ?? 0) || 0,
    isAvailable: row.is_available !== false,
    isOnline: row.is_online === true || row.isOnline === true,
    isFavorite: false,
    avatarColor: aeStr(row.avatar_color) || palette[idx % palette.length],
    expertise: aeArr(row.expertise),
    education: aeArr(row.education),
    availability: Array.isArray(row.availability) ? row.availability : aeArr(row.availability),
    languages: aeArr(row.languages),
    responseTime: aeStr(row.response_time) || aeStr(row.responseTime) || 'Within 24 hours',
    description: aeStr(row.description) || aeStr(row.bio) || '',
    achievements: aeArr(row.achievements),
    sessionsCompleted: Number(row.sessions_completed ?? 0) || 0,
    successRate: Number(row.success_rate ?? 0) || 0,
  };
};

const AskExpert = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const [userData, setUserData] = useState(null);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [scaleAnim] = useState(new Animated.Value(0.95));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTab, setSelectedTab] = useState('available');
  
  // Experts data
  const [experts, setExperts] = useState([]);
  const [filteredExperts, setFilteredExperts] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [availableExperts, setAvailableExperts] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  
  // Booking states
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [bookingDate, setBookingDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [bookingTime, setBookingTime] = useState('10:00 AM');
  const [bookingDuration, setBookingDuration] = useState(30);
  const [bookingNotes, setBookingNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [isBooking, setIsBooking] = useState(false);
  
  // Session details modal
  const [sessionModalVisible, setSessionModalVisible] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Quick chat modal
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const [chatExpert, setChatExpert] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Filter states
  const [priceFilter, setPriceFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('now');
  const [ratingFilter, setRatingFilter] = useState(0);
  const [sortBy, setSortBy] = useState('rating');
  const [showSortModal, setShowSortModal] = useState(false);
  
  // Animation refs
  const scrollY = useRef(new Animated.Value(0)).current;
  const chatScrollViewRef = useRef(null);
  const searchInputRef = useRef(null);
  
  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [1, 0.8],
    extrapolate: 'clamp',
  });

  const headerTranslateY = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [0, -20],
    extrapolate: 'clamp',
  });

  const headerScale = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [1, 0.95],
    extrapolate: 'clamp',
  });

  // Memoized data
  const timeSlots = useMemo(() => [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
    '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
    '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM',
  ], []);

  const durationOptions = useMemo(() => [15, 30, 45, 60], []);
  
  // Enhanced categories with gradients
  const categories = useMemo(() => [
    { 
      id: 'all', 
      label: 'All', 
      icon: 'account-group', 
      color: '#6366f1', 
      gradient: ['#6366f1', '#8b5cf6'] 
    },
    { 
      id: 'mechanical', 
      label: 'Mechanical', 
      icon: 'cog', 
      color: '#FF6B35', 
      gradient: ['#FF6B35', '#FF8E53'] 
    },
    { 
      id: 'civil', 
      label: 'Civil', 
      icon: 'bridge', 
      color: '#6366f1', 
      gradient: ['#6366f1', '#3b82f6'] 
    },
    { 
      id: 'electrical', 
      label: 'Electrical', 
      icon: 'flash', 
      color: '#FFD700', 
      gradient: ['#FFD700', '#FFE766'] 
    },
    { 
      id: 'computer', 
      label: 'Computer', 
      icon: 'laptop', 
      color: '#3776AB', 
      gradient: ['#3776AB', '#60a5fa'] 
    },
    { 
      id: 'chemical', 
      label: 'Chemical', 
      icon: 'flask', 
      color: '#9C27B0', 
      gradient: ['#9C27B0', '#d946ef'] 
    },
    { 
      id: 'aerospace', 
      label: 'Aerospace', 
      icon: 'airplane', 
      color: '#2196F3', 
      gradient: ['#2196F3', '#38bdf8'] 
    },
  ], []);

  const tabs = useMemo(() => [
    { id: 'available', label: 'Available', icon: 'clock-check' },
    { id: 'upcoming', label: 'Upcoming', icon: 'calendar' },
    { id: 'past', label: 'Past', icon: 'history' },
    { id: 'favorites', label: 'Favorites', icon: 'star' },
  ], []);

  // Modern gradient scheme
  const gradients = useMemo(() => darkMode ? {
    primary: ['#6366f1', '#8b5cf6'],
    secondary: ['#ec4899', '#d946ef'],
    success: ['#10b981', '#34d399'],
    warning: ['#f59e0b', '#fbbf24'],
    danger: ['#ef4444', '#f87171'],
    info: ['#3b82f6', '#60a5fa'],
    background: ['#0f172a', '#1e293b'],
    card: ['#1e293b', '#334155'],
    surface: ['#334155', '#475569'],
  } : {
    primary: ['#6366f1', '#8b5cf6'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4ade80', '#22c55e'],
    warning: ['#fbbf24', '#f59e0b'],
    danger: ['#f87171', '#ef4444'],
    info: ['#60a5fa', '#3b82f6'],
    background: ['#f8fafc', '#f1f5f9'],
    card: ['#ffffff', '#f8fafc'],
    surface: ['#f1f5f9', '#e2e8f0'],
  }, [darkMode]);

  // Create styles based on darkMode
  const styles = useMemo(() => StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    loadingText: {
      marginTop: 16,
      fontSize: 16,
      color: darkMode ? '#cbd5e0' : '#64748b',
      fontWeight: '500',
    },
    header: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
    },
    headerGradient: {
      paddingTop: (Platform.OS === 'ios' ? 56 : (StatusBar.currentHeight || 24) + 12),
      paddingBottom: 20,
      paddingHorizontal: 20,
    },
    headerTop: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    backButton: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: 'rgba(255,255,255,0.1)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    headerTitleContainer: {
      flex: 1,
      paddingHorizontal: 16,
    },
    headerSubtitle: {
      fontSize: 14,
      color: 'rgba(255,255,255,0.8)',
      fontWeight: '500',
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    notificationButton: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: 'rgba(255,255,255,0.1)',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
    },
    notificationBadge: {
      position: 'absolute',
      top: 8,
      right: 8,
      backgroundColor: '#ef4444',
      width: 18,
      height: 18,
      borderRadius: 9,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 2,
      borderColor: '#6366f1',
    },
    notificationBadgeText: {
      color: '#ffffff',
      fontSize: 10,
      fontWeight: 'bold',
    },
    searchContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: 16,
      paddingHorizontal: 16,
      marginBottom: 20,
      height: 52,
    },
    searchIcon: {
      marginRight: 12,
    },
    searchInput: {
      flex: 1,
      height: 52,
      fontSize: 16,
      color: '#ffffff',
    },
    clearButton: {
      padding: 4,
    },
    categoryContainer: {
      marginBottom: 10,
    },
    categoryButton: {
      alignItems: 'center',
      marginRight: 16,
    },
    categoryIconContainer: {
      width: 56,
      height: 56,
      borderRadius: 28,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 8,
      borderWidth: 2,
      borderColor: 'rgba(255,255,255,0.2)',
    },
    categoryIconContainerActive: {
      borderColor: '#ffffff',
      shadowColor: '#ffffff',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    categoryLabel: {
      fontSize: 12,
      color: 'rgba(255,255,255,0.8)',
      fontWeight: '500',
    },
    categoryLabelActive: {
      color: '#ffffff',
      fontWeight: '600',
    },
    tabContainer: {
      flexDirection: 'row',
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      marginHorizontal: 20,
      marginTop: (Platform.OS === 'ios' ? 230 : 210),
      borderRadius: 16,
      padding: 8,
      marginBottom: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    tabButton: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: 10,
      borderRadius: 16,
    },
    tabButtonActive: {
      backgroundColor: darkMode ? '#334155' : '#f1f5f9',
    },
    tabIconContainer: {
      width: 36,
      height: 36,
      borderRadius: 18,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 6,
    },
    tabLabel: {
      fontSize: 12,
      color: darkMode ? '#94a3b8' : '#64748b',
      fontWeight: '500',
    },
    tabLabelActive: {
      color: darkMode ? '#ffffff' : '#1e293b',
      fontWeight: '600',
    },
    scrollView: {
      flex: 1,
      marginTop: 0,
    },
    scrollContent: {
      paddingBottom: 100,
    },
    bottomPadding: {
      height: 80,
    },
    welcomeSection: {
      marginHorizontal: 20,
      marginBottom: 20,
      borderRadius: 20,
      overflow: 'hidden',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.2,
      shadowRadius: 16,
      elevation: 8,
    },
    welcomeGradient: {
      padding: 24,
    },
    welcomeContent: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    welcomeTextContainer: {
      flex: 1,
      marginRight: 16,
    },
    welcomeTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: 8,
    },
    welcomeSubtitle: {
      fontSize: 14,
      color: 'rgba(255,255,255,0.9)',
      lineHeight: 20,
    },
    welcomeStats: {
      flexDirection: 'row',
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: 16,
      padding: 12,
    },
    statItem: {
      alignItems: 'center',
      paddingHorizontal: 8,
    },
    statNumber: {
      fontSize: 18,
      fontWeight: 'bold',
      color: '#ffffff',
      marginVertical: 4,
    },
    statLabel: {
      fontSize: 11,
      color: 'rgba(255,255,255,0.8)',
      textTransform: 'uppercase',
    },
    statDivider: {
      width: 1,
      backgroundColor: 'rgba(255,255,255,0.2)',
      marginHorizontal: 8,
    },
    filterSection: {
      marginHorizontal: 20,
      marginBottom: 24,
    },
    filterButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 20,
      marginRight: 8,
      borderWidth: 1,
      borderColor: darkMode ? '#334155' : '#e2e8f0',
    },
    filterButtonActive: {
      backgroundColor: '#6366f1',
      borderColor: '#6366f1',
    },
    filterText: {
      marginLeft: 6,
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      fontWeight: '500',
    },
    filterTextActive: {
      color: '#ffffff',
    },
    section: {
      marginHorizontal: 20,
      marginBottom: 24,
    },
    sectionHeader: {
      marginBottom: 16,
    },
    sectionTitle: {
      fontSize: 22,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 4,
    },
    sectionSubtitle: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
    },
    expertCard: {
      marginBottom: 16,
      borderRadius: 20,
      overflow: 'hidden',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 6,
    },
    expertCardInner: {
      padding: 20,
    },
    expertHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 16,
    },
    expertAvatarSection: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    expertAvatar: {
      width: 60,
      height: 60,
      borderRadius: 30,
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
    },
    expertAvatarText: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    onlineIndicator: {
      position: 'absolute',
      bottom: 2,
      right: 2,
      width: 12,
      height: 12,
      borderRadius: 6,
      backgroundColor: '#10b981',
      borderWidth: 2,
      borderColor: darkMode ? '#1e293b' : '#ffffff',
    },
    expertStatus: {
      marginLeft: 12,
    },
    expertStatusText: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginBottom: 2,
    },
    expertStatusTime: {
      fontSize: 12,
      color: '#10b981',
      fontWeight: '500',
    },
    favoriteButton: {
      padding: 8,
    },
    expertInfo: {
      marginBottom: 16,
    },
    expertName: {
      fontSize: 20,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 4,
    },
    expertTitle: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#475569',
      marginBottom: 4,
    },
    expertCompany: {
      fontSize: 13,
      color: '#6366f1',
      fontWeight: '500',
      marginBottom: 8,
    },
    ratingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    ratingStars: {
      flexDirection: 'row',
      marginRight: 8,
    },
    ratingText: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#475569',
      fontWeight: '600',
      marginRight: 4,
    },
    reviewsText: {
      fontSize: 12,
      color: darkMode ? '#94a3b8' : '#64748b',
    },
    expertDetails: {
      marginBottom: 20,
    },
    expertiseSection: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    expertiseLabel: {
      fontSize: 13,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginLeft: 8,
      marginRight: 12,
    },
    expertiseTags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      flex: 1,
    },
    expertiseTag: {
      backgroundColor: darkMode ? '#334155' : '#f1f5f9',
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 16,
      marginRight: 8,
      marginBottom: 4,
    },
    expertiseTagText: {
      fontSize: 12,
      color: darkMode ? '#cbd5e0' : '#475569',
      fontWeight: '500',
    },
    moreExpertiseTag: {
      backgroundColor: '#6366f1',
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 16,
    },
    moreExpertiseText: {
      fontSize: 12,
      color: '#ffffff',
      fontWeight: '500',
    },
    experienceSection: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    experienceText: {
      fontSize: 13,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginLeft: 8,
    },
    expertFooter: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: darkMode ? '#334155' : '#e2e8f0',
    },
    priceSection: {
      flexDirection: 'row',
      alignItems: 'baseline',
    },
    priceText: {
      fontSize: 28,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
    },
    priceLabel: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginLeft: 4,
    },
    actionButtons: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    chatButton: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: darkMode ? '#334155' : '#f1f5f9',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    chatButtonDisabled: {
      opacity: 0.5,
    },
    bookButton: {
      borderRadius: 16,
      overflow: 'hidden',
    },
    bookButtonGradient: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 12,
    },
    bookButtonDisabled: {
      opacity: 0.7,
    },
    bookButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      marginRight: 8,
    },
    sessionCard: {
      marginBottom: 16,
      borderRadius: 20,
      overflow: 'hidden',
    },
    sessionCardInner: {
      padding: 20,
    },
    sessionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
    },
    sessionAvatar: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: '#6366f1',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    sessionAvatarText: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    sessionInfo: {
      flex: 1,
    },
    sessionExpertName: {
      fontSize: 16,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 2,
    },
    sessionSpecialization: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginBottom: 8,
    },
    sessionStatus: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 16,
    },
    sessionStatusText: {
      fontSize: 12,
      fontWeight: '500',
      marginLeft: 4,
    },
    sessionType: {
      width: 40,
      height: 40,
      borderRadius: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
    sessionDetails: {
      marginLeft: 60,
    },
    sessionDateTime: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    sessionDateText: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#475569',
      marginLeft: 8,
    },
    sessionTimeIcon: {
      marginLeft: 16,
    },
    sessionTimeText: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#475569',
      marginLeft: 8,
    },
    sessionNotesContainer: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 16,
    },
    sessionNotes: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginLeft: 8,
      flex: 1,
      lineHeight: 20,
    },
    sessionFooter: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    sessionPrice: {
      backgroundColor: darkMode ? '#334155' : '#f1f5f9',
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 16,
    },
    sessionPriceText: {
      fontSize: 16,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
    },
    joinButton: {
      borderRadius: 16,
      overflow: 'hidden',
    },
    joinButtonGradient: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 10,
    },
    joinButtonText: {
      color: '#ffffff',
      fontSize: 14,
      fontWeight: '600',
      marginRight: 6,
    },
    cancelButton: {
      paddingHorizontal: 20,
      paddingVertical: 10,
      borderRadius: 16,
      backgroundColor: darkMode ? '#334155' : '#f1f5f9',
    },
    cancelButtonText: {
      color: '#ef4444',
      fontSize: 14,
      fontWeight: '600',
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 48,
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      borderRadius: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 4,
      elevation: 2,
    },
    emptyStateTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginTop: 16,
    },
    emptyStateSubtitle: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      textAlign: 'center',
      marginTop: 8,
      marginBottom: 16,
      paddingHorizontal: 32,
    },
    emptyStateButton: {
      backgroundColor: '#6366f1',
      paddingHorizontal: 24,
      paddingVertical: 12,
      borderRadius: 16,
    },
    emptyStateButtonText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalScrollContent: {
      flexGrow: 1,
      justifyContent: 'flex-end',
      paddingTop: 40,
    },
    modalContent: {
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      borderTopLeftRadius: 28,
      borderTopRightRadius: 28,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 16,
      borderTopLeftRadius: 28,
      borderTopRightRadius: 28,
      overflow: 'hidden',
    },
    modalTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    closeButton: {
      padding: 4,
    },
    modalExpertInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 20,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#334155' : '#e2e8f0',
    },
    modalExpertAvatar: {
      width: 64,
      height: 64,
      borderRadius: 32,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 16,
    },
    modalExpertAvatarText: {
      fontSize: 24,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    modalExpertDetails: {
      flex: 1,
    },
    modalExpertName: {
      fontSize: 18,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 2,
    },
    modalExpertTitle: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginBottom: 4,
    },
    modalExpertPrice: {
      fontSize: 16,
      color: '#6366f1',
      fontWeight: '600',
    },
    bookingSection: {
      padding: 20,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#334155' : '#e2e8f0',
    },
    bookingSectionTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 12,
    },
    durationOptions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    durationOption: {
      backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
      padding: 12,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: darkMode ? '#334155' : '#e2e8f0',
      alignItems: 'center',
      flex: 1,
      minWidth: '22%',
    },
    durationOptionActive: {
      backgroundColor: '#6366f1',
      borderColor: '#6366f1',
    },
    durationText: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      fontWeight: '600',
    },
    durationTextActive: {
      color: '#ffffff',
    },
    durationPrice: {
      fontSize: 12,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginTop: 4,
    },
    durationPriceActive: {
      color: '#ffffff',
    },
    timeSelection: {
      gap: 12,
    },
    dateSelector: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
      padding: 12,
      borderRadius: 16,
    },
    dateText: {
      fontSize: 16,
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginLeft: 8,
      flex: 1,
    },
    dateChangeButton: {
      backgroundColor: darkMode ? '#334155' : '#e2e8f0',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
    },
    dateChangeText: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
    },
    timeSlotContainer: {
      marginTop: 8,
    },
    timeSlot: {
      backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 16,
      marginRight: 8,
      borderWidth: 1,
      borderColor: darkMode ? '#334155' : '#e2e8f0',
    },
    timeSlotActive: {
      backgroundColor: '#6366f1',
      borderColor: '#6366f1',
    },
    timeSlotText: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
    },
    timeSlotTextActive: {
      color: '#ffffff',
    },
    notesInput: {
      borderWidth: 1,
      borderColor: darkMode ? '#334155' : '#d1d5db',
      borderRadius: 16,
      paddingHorizontal: 16,
      paddingVertical: 12,
      fontSize: 16,
      color: darkMode ? '#f1f5f9' : '#0f172a',
      backgroundColor: darkMode ? '#1e293b' : '#f9fafb',
      minHeight: 100,
      textAlignVertical: 'top',
    },
    bookingSummary: {
      padding: 20,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#334155' : '#e2e8f0',
    },
    summaryTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 16,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    summaryLabel: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
    },
    summaryValue: {
      fontSize: 14,
      color: darkMode ? '#f1f5f9' : '#0f172a',
      fontWeight: '500',
    },
    totalRow: {
      marginTop: 12,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: darkMode ? '#334155' : '#e2e8f0',
    },
    totalLabel: {
      fontSize: 18,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
    },
    totalValue: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#6366f1',
    },
    modalActions: {
      flexDirection: 'row',
      padding: 20,
      paddingBottom: Platform.OS === 'ios' ? 36 : 24,
    },
    cancelBookingButton: {
      flex: 1,
      paddingVertical: 14,
      alignItems: 'center',
      marginRight: 12,
    },
    cancelBookingText: {
      fontSize: 16,
      color: darkMode ? '#94a3b8' : '#64748b',
      fontWeight: '600',
    },
    confirmBookingButton: {
      flex: 2,
      borderRadius: 16,
      overflow: 'hidden',
    },
    confirmBookingGradient: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
    },
    confirmBookingText: {
      fontSize: 16,
      color: '#ffffff',
      fontWeight: '600',
      marginRight: 8,
    },
    sessionModalContainer: {
      flex: 1,
      backgroundColor: darkMode ? '#0f172a' : '#f0f4f8',
    },
    sessionModalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 16,
    },
    sessionModalTitle: {
      flex: 1,
      fontSize: 18,
      fontWeight: 'bold',
      color: '#ffffff',
      marginHorizontal: 12,
    },
    headerSpacer: {
      width: 40,
    },
    sessionModalContent: {
      flex: 1,
      padding: 16,
    },
    sessionDetailCard: {
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      borderRadius: 20,
      padding: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    detailHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 24,
    },
    detailAvatar: {
      width: 60,
      height: 60,
      borderRadius: 30,
      backgroundColor: '#6366f1',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 16,
    },
    detailAvatarText: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    detailExpertName: {
      fontSize: 18,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 2,
    },
    detailSpecialization: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      marginBottom: 8,
    },
    statusBadge: {
      alignSelf: 'flex-start',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 8,
    },
    statusBadgeText: {
      fontSize: 12,
      color: '#ffffff',
      fontWeight: '600',
    },
    detailSection: {
      marginBottom: 24,
    },
    detailSectionTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 12,
    },
    detailItem: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    detailText: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#475569',
      marginLeft: 8,
    },
    notesText: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#475569',
      lineHeight: 20,
    },
    preparationItem: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    preparationText: {
      fontSize: 14,
      color: darkMode ? '#cbd5e0' : '#475569',
      marginLeft: 8,
    },
    meetingLinkButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#6366f1',
      paddingVertical: 12,
      borderRadius: 16,
    },
    meetingLinkText: {
      color: '#ffffff',
      fontSize: 16,
      fontWeight: '600',
      marginRight: 8,
    },
    recordingButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: darkMode ? '#1e293b' : '#f1f5f9',
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: 16,
    },
    recordingText: {
      fontSize: 16,
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginLeft: 8,
      fontWeight: '500',
    },
    cancelSessionButton: {
      backgroundColor: darkMode ? '#334155' : '#f1f5f9',
      paddingVertical: 12,
      borderRadius: 16,
      alignItems: 'center',
      marginTop: 16,
    },
    cancelSessionButtonText: {
      color: '#ef4444',
      fontSize: 16,
      fontWeight: '600',
    },
    chatModalContainer: {
      flex: 1,
      backgroundColor: darkMode ? '#0f172a' : '#f0f4f8',
    },
    chatHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 20,
      paddingVertical: 16,
    },
    chatCloseButton: {
      padding: 8,
    },
    chatExpertInfo: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    chatExpertAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    chatExpertAvatarText: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    chatExpertName: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    chatExpertStatus: {
      fontSize: 12,
      color: '#ffffff',
      opacity: 0.9,
    },
    chatMenuButton: {
      padding: 8,
    },
    chatMessages: {
      flex: 1,
      padding: 16,
    },
    messageContainer: {
      flexDirection: 'row',
      marginBottom: 16,
      alignItems: 'flex-end',
    },
    expertMessageContainer: {
      justifyContent: 'flex-start',
    },
    userMessageContainer: {
      justifyContent: 'flex-end',
    },
    expertChatAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: 'rgba(102, 126, 234, 0.2)',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 8,
    },
    expertChatAvatarText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#ffffff',
    },
    messageBubble: {
      maxWidth: '75%',
      padding: 12,
      borderRadius: 16,
    },
    expertMessageBubble: {
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      borderBottomLeftRadius: 4,
    },
    userMessageBubble: {
      backgroundColor: '#6366f1',
      borderBottomRightRadius: 4,
    },
    messageText: {
      fontSize: 15,
      lineHeight: 20,
      marginBottom: 4,
    },
    expertMessageText: {
      color: darkMode ? '#f1f5f9' : '#0f172a',
    },
    userMessageText: {
      color: '#ffffff',
    },
    messageTime: {
      fontSize: 11,
      opacity: 0.7,
      color: darkMode ? '#94a3b8' : '#64748b',
    },
    typingIndicator: {
      padding: 12,
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      borderRadius: 16,
      alignSelf: 'flex-start',
      marginBottom: 16,
    },
    typingText: {
      fontSize: 14,
      color: darkMode ? '#94a3b8' : '#64748b',
      fontStyle: 'italic',
    },
    chatInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 16,
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      borderTopWidth: 1,
      borderTopColor: darkMode ? '#334155' : '#e2e8f0',
    },
    chatInput: {
      flex: 1,
      borderWidth: 1,
      borderColor: darkMode ? '#334155' : '#d1d5db',
      borderRadius: 20,
      paddingHorizontal: 16,
      paddingVertical: 12,
      fontSize: 16,
      color: darkMode ? '#f1f5f9' : '#0f172a',
      backgroundColor: darkMode ? '#0f172a' : '#f9fafb',
      marginRight: 12,
      maxHeight: 100,
    },
    sendButton: {
      borderRadius: 20,
      overflow: 'hidden',
    },
    sendButtonGradient: {
      width: 40,
      height: 40,
      justifyContent: 'center',
      alignItems: 'center',
    },
    sortModalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    sortModalContent: {
      backgroundColor: darkMode ? '#1e293b' : '#ffffff',
      borderRadius: 20,
      padding: 24,
      width: '80%',
      maxWidth: 400,
    },
    sortModalTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: darkMode ? '#f1f5f9' : '#0f172a',
      marginBottom: 20,
      textAlign: 'center',
    },
    sortOption: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: darkMode ? '#334155' : '#e2e8f0',
    },
    sortOptionText: {
      fontSize: 16,
      color: darkMode ? '#cbd5e0' : '#475569',
    },
    sortOptionTextActive: {
      color: '#6366f1',
      fontWeight: '600',
    },
    closeSortButton: {
      marginTop: 20,
      paddingVertical: 12,
      alignItems: 'center',
      borderTopWidth: 1,
      borderTopColor: darkMode ? '#334155' : '#e2e8f0',
    },
    closeSortText: {
      fontSize: 16,
      color: '#6366f1',
      fontWeight: '600',
    },
    fab: {
      position: 'absolute',
      bottom: 24,
      right: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.3,
      shadowRadius: 16,
      elevation: 8,
    },
    fabGradient: {
      width: 60,
      height: 60,
      borderRadius: 30,
      justifyContent: 'center',
      alignItems: 'center',
    },
  }), [darkMode]);

  useEffect(() => {
    loadInitialData();
    startAnimations();
    requestNotificationPermissions();
    
    return () => {
      // Cleanup if needed
    };
  }, []);

  useEffect(() => {
    filterAndSortExperts();
  }, [searchQuery, selectedCategory, priceFilter, availabilityFilter, ratingFilter, sortBy, experts]);

  useFocusEffect(
    useCallback(() => {
      refreshData();
    }, [])
  );

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadUserData(),
        loadExperts(),
        loadSessions(),
        loadDarkModePreference(),
      ]);
    } catch (error) {
      console.error('Error loading expert data:', error);
      Alert.alert('Error', 'Failed to load expert data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadExperts(),
        loadSessions(),
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
      Alert.alert('Error', 'Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const requestNotificationPermissions = async () => {
    try {
      const { status } = await Notifications.requestPermissionsAsync();
      if (status !== 'granted') {
        console.log('Notification permission not granted');
      }
    } catch (error) {
      console.error('Error requesting notification permissions:', error);
    }
  };

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      if (storedUser) {
        setUserData(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadExperts = async () => {
    try {
      const SEED_EXPERTS = [
        {
          id: 'EXP001',
          name: 'Dr. Sarah Johnson',
          title: 'Senior Structural Engineer',
          company: 'Arup Engineering',
          experience: '15 years',
          specialization: 'Civil Engineering',
          category: 'civil',
          rating: 4.9,
          reviews: 127,
          pricePerHour: 120,
          isAvailable: true,
          isOnline: true,
          isFavorite: true,
          avatarColor: '#6366f1',
          expertise: ['Structural Analysis', 'Seismic Design', 'Bridge Engineering', 'Finite Element Analysis'],
          education: ['PhD in Civil Engineering, MIT', 'MSc in Structural Engineering, Stanford'],
          availability: [
            { day: 'Mon', hours: '9 AM - 5 PM' },
            { day: 'Wed', hours: '10 AM - 6 PM' },
            { day: 'Fri', hours: '9 AM - 4 PM' }
          ],
          languages: ['English', 'Spanish'],
          responseTime: 'Within 2 hours',
          description: 'Specialized in structural design and analysis with extensive experience in earthquake-resistant structures.',
          achievements: ['PE Licensed Engineer', 'Published 25+ research papers', 'Awarded Engineering Excellence 2022'],
          sessionsCompleted: 245,
          successRate: 98,
        },
        {
          id: 'EXP002',
          name: 'Prof. Robert Williams',
          title: 'Mechanical Engineering Professor',
          company: 'Imperial College London',
          experience: '22 years',
          specialization: 'Mechanical Engineering',
          category: 'mechanical',
          rating: 4.8,
          reviews: 89,
          pricePerHour: 95,
          isAvailable: true,
          isOnline: true,
          isFavorite: false,
          avatarColor: '#FF6B35',
          expertise: ['Thermodynamics', 'Fluid Mechanics', 'CAD Design', 'Mechatronics'],
          education: ['PhD in Mechanical Engineering, Cambridge', 'MEng in Automotive Engineering'],
          availability: [
            { day: 'Tue', hours: '10 AM - 7 PM' },
            { day: 'Thu', hours: '9 AM - 5 PM' },
            { day: 'Sat', hours: '11 AM - 3 PM' }
          ],
          languages: ['English', 'French'],
          responseTime: 'Within 4 hours',
          description: 'Expert in mechanical system design and thermal analysis with industry and academic experience.',
          achievements: ['Fellow of IMechE', '3 Patent Awards', 'Industry Consultant for BMW'],
          sessionsCompleted: 189,
          successRate: 96,
        },
        {
          id: 'EXP003',
          name: 'Eng. Maria Rodriguez',
          title: 'Electrical Systems Lead',
          company: 'Siemens Energy',
          experience: '12 years',
          specialization: 'Electrical Engineering',
          category: 'electrical',
          rating: 4.7,
          reviews: 64,
          pricePerHour: 85,
          isAvailable: false,
          isOnline: false,
          isFavorite: true,
          avatarColor: '#FFD700',
          expertise: ['Power Systems', 'Renewable Energy', 'Control Systems', 'Smart Grids'],
          education: ['MSc in Electrical Engineering, ETH Zurich', 'BSc in Power Engineering'],
          availability: [
            { day: 'Mon', hours: '8 AM - 4 PM' },
            { day: 'Wed', hours: '9 AM - 5 PM' },
            { day: 'Fri', hours: '10 AM - 6 PM' }
          ],
          languages: ['English', 'German', 'Spanish'],
          responseTime: 'Within 24 hours',
          description: 'Specialized in power systems and renewable energy integration for sustainable solutions.',
          achievements: ['IEEE Senior Member', 'Green Energy Innovator Award', 'Grid Modernization Project Lead'],
          sessionsCompleted: 156,
          successRate: 95,
        },
        {
          id: 'EXP004',
          name: 'Dr. Alex Chen',
          title: 'AI Research Scientist',
          company: 'Google DeepMind',
          experience: '8 years',
          specialization: 'Computer Engineering',
          category: 'computer',
          rating: 4.9,
          reviews: 142,
          pricePerHour: 150,
          isAvailable: true,
          isOnline: true,
          isFavorite: false,
          avatarColor: '#3776AB',
          expertise: ['Machine Learning', 'Computer Vision', 'AI Ethics', 'Deep Learning'],
          education: ['PhD in Computer Science, Stanford', 'MSc in Artificial Intelligence'],
          availability: [
            { day: 'Mon', hours: '2 PM - 8 PM' },
            { day: 'Tue', hours: '3 PM - 9 PM' },
            { day: 'Thu', hours: '1 PM - 7 PM' }
          ],
          languages: ['English', 'Mandarin'],
          responseTime: 'Within 1 hour',
          description: 'Focus on applied AI solutions for engineering problems and ethical AI development.',
          achievements: ['NeurIPS Best Paper Award', 'Google Research Excellence', 'Open Source AI Projects'],
          sessionsCompleted: 278,
          successRate: 99,
        },
        {
          id: 'EXP005',
          name: 'Dr. James Wilson',
          title: 'Chemical Process Engineer',
          company: 'BASF',
          experience: '18 years',
          specialization: 'Chemical Engineering',
          category: 'chemical',
          rating: 4.6,
          reviews: 56,
          pricePerHour: 110,
          isAvailable: true,
          isOnline: false,
          isFavorite: false,
          avatarColor: '#9C27B0',
          expertise: ['Process Design', 'Reactor Engineering', 'Safety Analysis', 'Simulation'],
          education: ['PhD in Chemical Engineering, UC Berkeley', 'MSc in Process Engineering'],
          availability: [
            { day: 'Tue', hours: '9 AM - 5 PM' },
            { day: 'Thu', hours: '10 AM - 6 PM' },
            { day: 'Sat', hours: '10 AM - 2 PM' }
          ],
          languages: ['English', 'German'],
          responseTime: 'Within 6 hours',
          description: 'Expert in chemical process optimization and industrial plant design with safety focus.',
          achievements: ['Chartered Chemical Engineer', 'Process Safety Award', 'Published Process Patents'],
          sessionsCompleted: 134,
          successRate: 94,
        },
        {
          id: 'EXP006',
          name: 'Capt. Emily Davis',
          title: 'Aerospace Systems Engineer',
          company: 'NASA JPL',
          experience: '14 years',
          specialization: 'Aerospace Engineering',
          category: 'aerospace',
          rating: 4.9,
          reviews: 78,
          pricePerHour: 135,
          isAvailable: false,
          isOnline: true,
          isFavorite: true,
          avatarColor: '#2196F3',
          expertise: ['Aerodynamics', 'Flight Control Systems', 'Space Systems', 'Propulsion'],
          education: ['MSc in Aerospace Engineering, Caltech', 'BSc in Aeronautics'],
          availability: [
            { day: 'Mon', hours: '11 AM - 7 PM' },
            { day: 'Wed', hours: '10 AM - 6 PM' },
            { day: 'Fri', hours: '9 AM - 5 PM' }
          ],
          languages: ['English', 'Russian'],
          responseTime: 'Within 3 hours',
          description: 'Specialized in spacecraft systems and aerodynamics with hands-on NASA project experience.',
          achievements: ['NASA Exceptional Achievement', 'Mars Rover Team Lead', 'Aerospace Patent Holder'],
          sessionsCompleted: 212,
          successRate: 97,
        },
      ];

      // Try the backend; fall back to the curated seed so the screen is
      // never blank. /api/data/experts is the experts directory table.
      let list = SEED_EXPERTS;
      try {
        const res = await fetch(`${AE_API_BASE}/api/data/experts?limit=100`, {
          headers: { 'x-api-key': AE_API_KEY },
        }).then((r) => r.json()).catch(() => null);
        if (res && res.success && Array.isArray(res.data) && res.data.length) {
          list = res.data.map(aeMapExpert);
        }
      } catch (e) {
        // keep seed
      }

      setExperts(list);
    } catch (error) {
      console.error('Error loading experts:', error);
      Alert.alert('Error', 'Failed to load experts. Please check your connection.');
    }
  };

  const filterAndSortExperts = useCallback(() => {
    let filtered = [...experts];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(expert =>
        String(expert.name ?? '').toLowerCase().includes(query) ||
        String(expert.title ?? '').toLowerCase().includes(query) ||
        String(expert.specialization ?? '').toLowerCase().includes(query) ||
        String(expert.company ?? '').toLowerCase().includes(query) ||
        expert.expertise.some(skill => skill.toLowerCase().includes(query))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(expert => expert.category === selectedCategory);
    }
    
    // Apply price filter
    if (priceFilter !== 'all') {
      if (priceFilter === '0-50') {
        filtered = filtered.filter(expert => expert.pricePerHour <= 50);
      } else if (priceFilter === '50-100') {
        filtered = filtered.filter(expert => expert.pricePerHour > 50 && expert.pricePerHour <= 100);
      } else if (priceFilter === '100+') {
        filtered = filtered.filter(expert => expert.pricePerHour > 100);
      }
    }
    
    // Apply availability filter
    if (availabilityFilter === 'now') {
      filtered = filtered.filter(expert => expert.isAvailable && expert.isOnline);
    } else if (availabilityFilter === 'today') {
      filtered = filtered.filter(expert => expert.isAvailable);
    }
    
    // Apply rating filter
    if (ratingFilter > 0) {
      filtered = filtered.filter(expert => expert.rating >= ratingFilter);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'price_low':
          return a.pricePerHour - b.pricePerHour;
        case 'price_high':
          return b.pricePerHour - a.pricePerHour;
        case 'experience':
          const expA = parseInt(a.experience) || 0;
          const expB = parseInt(b.experience) || 0;
          return expB - expA;
        case 'popularity':
          return b.reviews - a.reviews;
        default:
          return b.rating - a.rating;
      }
    });
    
    setFilteredExperts(filtered);
    setAvailableExperts(filtered.filter(expert => expert.isAvailable));
  }, [experts, searchQuery, selectedCategory, priceFilter, availabilityFilter, ratingFilter, sortBy]);

  const loadSessions = async () => {
    try {
      const mockUpcomingSessions = [
        {
          id: 'SESS001',
          expertId: 'EXP001',
          expertName: 'Dr. Sarah Johnson',
          expertSpecialization: 'Civil Engineering',
          date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
          time: '2:00 PM',
          duration: 60,
          status: 'confirmed',
          type: 'video',
          meetingLink: 'https://meet.google.com/xyz-structural',
          price: 120,
          notes: 'Need help with bridge design project',
          preparation: [
            'Project documents',
            'CAD files',
            'Load calculations'
          ]
        },
        {
          id: 'SESS002',
          expertId: 'EXP004',
          expertName: 'Dr. Alex Chen',
          expertSpecialization: 'Computer Engineering',
          date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
          time: '4:30 PM',
          duration: 45,
          status: 'confirmed',
          type: 'voice',
          meetingLink: 'https://zoom.us/j/ai-help-session',
          price: 112.5,
          notes: 'AI model optimization discussion',
          preparation: [
            'Code repository',
            'Dataset details',
            'Performance metrics'
          ]
        },
      ];
      
      const mockPastSessions = [
        {
          id: 'SESS003',
          expertId: 'EXP002',
          expertName: 'Prof. Robert Williams',
          expertSpecialization: 'Mechanical Engineering',
          date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          time: '11:00 AM',
          duration: 30,
          status: 'completed',
          type: 'video',
          meetingLink: null,
          price: 47.5,
          notes: 'Thermodynamics problem solving',
          rating: 5,
          review: 'Excellent explanation! Really helped me understand the concepts.',
          recordingAvailable: true
        },
        {
          id: 'SESS004',
          expertId: 'EXP003',
          expertName: 'Eng. Maria Rodriguez',
          expertSpecialization: 'Electrical Engineering',
          date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
          time: '3:00 PM',
          duration: 60,
          status: 'completed',
          type: 'chat',
          meetingLink: null,
          price: 85,
          notes: 'Power systems consultation',
          rating: 4,
          review: 'Very knowledgeable, but connection was unstable.',
          recordingAvailable: false
        },
      ];
      
      setUpcomingSessions(mockUpcomingSessions);
      setPastSessions(mockPastSessions);
      setMySessions([...mockUpcomingSessions, ...mockPastSessions]);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const getUserName = () => {
    if (!userData) return 'Student';
    const fullName = userData.full_name || userData.fullName || userData.name;
    return fullName || 'Student';
  };

  const toggleFavorite = async (expertId) => {
    const updatedExperts = experts.map(expert => {
      if (expert.id === expertId) {
        return { ...expert, isFavorite: !expert.isFavorite };
      }
      return expert;
    });
    
    setExperts(updatedExperts);
    
    try {
      await AsyncStorage.setItem('favoriteExperts', JSON.stringify(
        updatedExperts.filter(e => e.isFavorite).map(e => e.id)
      ));
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  };

  const openBookingModal = (expert) => {
    setSelectedExpert(expert);
    setBookingDate(new Date());
    setBookingTime(timeSlots[2]);
    setBookingDuration(30);
    setBookingNotes('');
    setBookingModalVisible(true);
  };

  const openSessionModal = (session) => {
    setSelectedSession(session);
    setSessionModalVisible(true);
  };

  const openQuickChat = (expert) => {
    setChatExpert(expert);
    setChatMessages([
      {
        id: 1,
        text: `Hello! I'm ${expert.name}, ${expert.title} at ${expert.company}. How can I help you with your ${expert.specialization} questions?`,
        isExpert: true,
        timestamp: new Date(),
      }
    ]);
    setInputMessage('');
    setChatModalVisible(true);
    
    // Scroll to bottom after chat loads
    setTimeout(() => {
      if (chatScrollViewRef.current) {
        chatScrollViewRef.current.scrollToEnd({ animated: true });
      }
    }, 100);
  };

  const bookSession = async () => {
    if (!selectedExpert) return;

    const totalPrice = (selectedExpert.pricePerHour * bookingDuration) / 60;

    // Paid session → go through the payment screen first. The session is only
    // created/persisted after a successful payment (handled on return below).
    if (totalPrice > 0) {
      const pendingBooking = {
        expertId: selectedExpert.id,
        expertName: selectedExpert.name,
        expertSpecialization: selectedExpert.specialization,
        date: bookingDate instanceof Date ? bookingDate.toISOString() : String(bookingDate),
        time: bookingTime,
        duration: bookingDuration,
        price: totalPrice,
        notes: bookingNotes,
      };
      const scenario = {
        id:            `expert_${selectedExpert.id}_${Date.now()}`,
        type:          'expert_session',
        category:      'learning',
        name:          `Session with ${selectedExpert.name}`,
        description:   `${bookingDuration}-minute session with ${selectedExpert.name}`,
        amountInPence: Math.round(totalPrice * 100),
        currency:      'GBP',
        metadata: {
          expert_id:    selectedExpert.id,
          expert_name:  selectedExpert.name,
          duration:     bookingDuration,
        },
        successRoute:  'AskExpert',
        successParams: { bookedSession: pendingBooking },
        successMessage: `Your session with ${selectedExpert.name} is confirmed.`,
      };
      setBookingModalVisible(false);
      navigation.navigate('PaymentScreen', { scenario });
      return;
    }

    // Free session → book immediately.
    setIsBooking(true);
    try {
      await finalizeBooking({
        expertId: selectedExpert.id,
        expertName: selectedExpert.name,
        expertSpecialization: selectedExpert.specialization,
        date: bookingDate,
        time: bookingTime,
        duration: bookingDuration,
        price: totalPrice,
        notes: bookingNotes,
      });
      setBookingModalVisible(false);
      setSelectedExpert(null);
    } catch (error) {
      console.error('Error booking session:', error);
      Alert.alert('Error', 'Failed to book session. Please try again.');
    } finally {
      setIsBooking(false);
    }
  };

  // Creates, persists, notifies and calendars a session. Used by both the free
  // path (directly) and the paid path (after payment returns).
  const finalizeBooking = async (b) => {
    const bDate = b.date instanceof Date ? b.date : new Date(b.date);
    const newSession = {
      id: `SESS${Date.now()}`,
      expertId: b.expertId,
      expertName: b.expertName,
      expertSpecialization: b.expertSpecialization,
      date: bDate,
      time: b.time,
      duration: b.duration,
      status: 'confirmed',
      type: 'video',
      meetingLink: `https://meet.google.com/${Math.random().toString(36).substr(2, 9)}`,
      price: b.price,
      notes: b.notes,
      preparation: [
        'Prepare your questions',
        'Have relevant materials ready',
        'Test your audio/video setup',
      ],
    };

    setUpcomingSessions(prev => [newSession, ...prev]);
    setMySessions(prev => [newSession, ...prev]);

    // Best-effort backend persistence (non-blocking).
    try {
      const token = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('userToken')) || '';
      const userId = (await AsyncStorage.getItem('user_id')) || (await AsyncStorage.getItem('userId')) || null;
      fetch(`${AE_API_BASE}/api/data/expert_bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': AE_API_KEY,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          session_id: newSession.id,
          expert_id: b.expertId,
          expert_name: b.expertName,
          user_id: userId,
          date: bDate.toISOString(),
          time: b.time,
          duration: b.duration,
          price: b.price,
          status: 'confirmed',
          notes: b.notes,
          created_at: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch {}

    try { await scheduleSessionNotification(newSession); } catch {}
    try { await addToCalendar(newSession); } catch {}

    Alert.alert(
      '🎉 Session Booked Successfully!',
      `Your ${b.duration}-minute session with ${b.expertName} has been booked for ${formatDate(bDate)} at ${b.time}.\n\nTotal: £${Number(b.price).toFixed(2)}`,
      [
        { text: 'View Details', onPress: () => openSessionModal(newSession) },
        { text: 'OK', style: 'default' },
      ]
    );
    return newSession;
  };

  // After a successful payment, PaymentScreen routes back here with the
  // booking details. Finalize it once, then clear the param.
  useEffect(() => {
    const b = route?.params?.bookedSession;
    if (!b) return;
    (async () => {
      try { await finalizeBooking(b); } catch (e) { console.error('Post-payment booking failed:', e); }
      finally {
        setSelectedExpert(null);
        navigation.setParams({ bookedSession: undefined });
      }
    })();
  }, [route?.params?.bookedSession]);

  const scheduleSessionNotification = async (session) => {
    try {
      const notificationTime = new Date(session.date);
      const [time, modifier] = session.time.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      
      if (modifier === 'PM' && hours < 12) hours += 12;
      if (modifier === 'AM' && hours === 12) hours = 0;
      
      notificationTime.setHours(hours, minutes, 0, 0);
      const reminderTime = new Date(notificationTime.getTime() - 30 * 60 * 1000);
      
      if (reminderTime > new Date()) {
        await Notifications.scheduleNotificationAsync({
          content: {
            title: '🎓 Expert Session Reminder',
            body: `Your session with ${session.expertName} starts in 30 minutes`,
            data: { sessionId: session.id, type: 'expert-session-reminder' },
            sound: 'default',
          },
          trigger: {
            type: 'date',
            date: reminderTime,
          },
        });
      }
    } catch (error) {
      console.error('Error scheduling notification:', error);
    }
  };

  const addToCalendar = async (session) => {
    try {
      const { status } = await Calendar.requestCalendarPermissionsAsync();
      if (status === 'granted') {
        const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
        const defaultCalendar = calendars.find(c => c.isPrimary) || calendars[0];
        
        if (defaultCalendar) {
          const [time, modifier] = session.time.split(' ');
          let [hours, minutes] = time.split(':').map(Number);
          
          if (modifier === 'PM' && hours < 12) hours += 12;
          if (modifier === 'AM' && hours === 12) hours = 0;
          
          const startDate = new Date(session.date);
          startDate.setHours(hours, minutes, 0, 0);
          const endDate = new Date(startDate.getTime() + session.duration * 60 * 1000);
          
          await Calendar.createEventAsync(defaultCalendar.id, {
            title: `Expert Session: ${session.expertName}`,
            startDate: startDate,
            endDate: endDate,
            location: 'Online',
            notes: `${session.expertSpecialization} consultation\nNotes: ${session.notes}\nPrice: £${session.price}`,
            url: session.meetingLink,
          });
        }
      }
    } catch (error) {
      console.error('Error adding to calendar:', error);
    }
  };

  const cancelSession = async (sessionId) => {
    Alert.alert(
      'Cancel Session',
      'Are you sure you want to cancel this session?',
      [
        { text: 'No', style: 'cancel' },
        {
          text: 'Yes, Cancel',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedSessions = upcomingSessions.filter(session => session.id !== sessionId);
              setUpcomingSessions(updatedSessions);
              setMySessions(prev => prev.filter(session => session.id !== sessionId));
              
              // Cancel notification
              await Notifications.cancelScheduledNotificationAsync(sessionId);
              
              Alert.alert('Cancelled', 'Your session has been cancelled.');
              setSessionModalVisible(false);
            } catch (error) {
              Alert.alert('Error', 'Failed to cancel session.');
            }
          }
        },
      ]
    );
  };

  const sendChatMessage = () => {
    if (!inputMessage.trim() || !chatExpert) return;
    
    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isExpert: false,
      timestamp: new Date(),
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    
    // Simulate expert typing
    setIsTyping(true);
    
    // Simulate expert response after delay
    setTimeout(() => {
      setIsTyping(false);
      
      const responses = [
        "That's an excellent question! Let me break it down for you.",
        "I understand your concern. Based on my experience, I would suggest...",
        "Great question! Here's how we typically approach this in industry...",
        "I'd be happy to help with that. Let me explain the key concepts...",
        "That's a common challenge. Here are some proven strategies...",
      ];
      
      const expertResponse = {
        id: Date.now() + 1,
        text: responses[Math.floor(Math.random() * responses.length)],
        isExpert: true,
        timestamp: new Date(),
      };
      
      setChatMessages(prev => [...prev, expertResponse]);
      
      // Scroll to bottom
      if (chatScrollViewRef.current) {
        chatScrollViewRef.current.scrollToEnd({ animated: true });
      }
    }, 2000);
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    let interval = Math.floor(seconds / 31536000);
    if (interval >= 1) return `${interval}y ago`;
    
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) return `${interval}mo ago`;
    
    interval = Math.floor(seconds / 86400);
    if (interval >= 1) return `${interval}d ago`;
    
    interval = Math.floor(seconds / 3600);
    if (interval >= 1) return `${interval}h ago`;
    
    interval = Math.floor(seconds / 60);
    if (interval >= 1) return `${interval}m ago`;
    
    return 'just now';
  };

  const getCategoryGradient = (category) => {
    const categoryObj = categories.find(cat => cat.id === category);
    return categoryObj ? categoryObj.gradient : gradients.primary;
  };

  const renderLoading = () => (
    <LinearGradient colors={gradients.background} style={styles.loadingContainer}>
      <ActivityIndicator size={48} color="#6366f1" />
      <Text style={styles.loadingText}>Loading expert network...</Text>
    </LinearGradient>
  );

  const renderHeader = () => (
    <Animated.View style={[
      styles.header,
      {
        opacity: headerOpacity,
        transform: [{ translateY: headerTranslateY }, { scale: headerScale }],
      },
    ]}>
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.backButton} 
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="chevron-back" size={28} color="#ffffff" />
          </TouchableOpacity>
          <View style={styles.headerTitleContainer}>
            <Text style={styles.headerSubtitle}>Connect with</Text>
            <Text style={styles.headerTitle}>Industry Experts</Text>
          </View>
          <TouchableOpacity 
            style={styles.notificationButton}
            onPress={() => navigation.navigate('Notifications')}
          >
            <Ionicons name="notifications-outline" size={26} color="#ffffff" />
            <View style={styles.notificationBadge}>
              <Text style={styles.notificationBadgeText}>2</Text>
            </View>
          </TouchableOpacity>
        </View>
        
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={22} color="#cbd5e0" style={styles.searchIcon} />
          <TextInput
            ref={searchInputRef}
            style={styles.searchInput}
            placeholder="Search experts, topics, or specializations..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#cbd5e0"
            returnKeyType="search"
            onSubmitEditing={() => Keyboard.dismiss()}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity style={styles.clearButton} onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#cbd5e0" />
            </TouchableOpacity>
          )}
        </View>
        
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false} 
          style={styles.categoryContainer}
        >
          {categories.map((category) => (
            <TouchableOpacity
              key={category.id}
              onPress={() => setSelectedCategory(category.id)}
              style={styles.categoryButton}
            >
              <LinearGradient
                colors={selectedCategory === category.id ? category.gradient : ['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.05)']}
                style={[
                  styles.categoryIconContainer,
                  selectedCategory === category.id && styles.categoryIconContainerActive
                ]}
              >
                <MaterialCommunityIcons 
                  name={category.icon} 
                  size={22} 
                  color={selectedCategory === category.id ? '#ffffff' : 'rgba(255,255,255,0.8)'} 
                />
              </LinearGradient>
              <Text style={[
                styles.categoryLabel,
                selectedCategory === category.id && styles.categoryLabelActive
              ]}>
                {category.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </LinearGradient>
    </Animated.View>
  );

  const renderTabNavigation = () => (
    <Animated.View 
      style={[
        styles.tabContainer,
        {
          transform: [{ scale: scaleAnim }],
        },
      ]}
    >
      {tabs.map((tab) => (
        <TouchableOpacity
          key={tab.id}
          style={[
            styles.tabButton,
            selectedTab === tab.id && styles.tabButtonActive,
          ]}
          onPress={() => setSelectedTab(tab.id)}
        >
          <LinearGradient
            colors={selectedTab === tab.id ? gradients.primary : ['transparent', 'transparent']}
            style={styles.tabIconContainer}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={selectedTab === tab.id ? '#ffffff' : (darkMode ? '#94a3b8' : '#64748b')} 
            />
          </LinearGradient>
          <Text style={[
            styles.tabLabel,
            selectedTab === tab.id && styles.tabLabelActive
          ]}>
            {tab.label}
          </Text>
        </TouchableOpacity>
      ))}
    </Animated.View>
  );

  const renderExpertCard = ({ item: expert, index }) => (
    <Animated.View 
      style={[
        styles.expertCard,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      key={`expert-${expert.id}-${index}`}
    >
      <LinearGradient
        colors={gradients.card}
        style={styles.expertCardInner}
      >
        <View style={styles.expertHeader}>
          <View style={styles.expertAvatarSection}>
            <LinearGradient
              colors={[expert.avatarColor, `${expert.avatarColor}80`]}
              style={styles.expertAvatar}
            >
              <Text style={styles.expertAvatarText}>
                {expert.name.split(' ').map(n => n[0]).join('')}
              </Text>
              {expert.isOnline && (
                <View style={styles.onlineIndicator} />
              )}
            </LinearGradient>
            <View style={styles.expertStatus}>
              <Text style={styles.expertStatusText}>
                {expert.isOnline ? '🟢 Online' : '🔴 Offline'}
              </Text>
              <Text style={styles.expertStatusTime}>
                {expert.isAvailable ? 'Available now' : 'Available soon'}
              </Text>
            </View>
          </View>
          
          <TouchableOpacity 
            style={styles.favoriteButton}
            onPress={() => toggleFavorite(expert.id)}
          >
            <Ionicons 
              name={expert.isFavorite ? "heart" : "heart-outline"} 
              size={24} 
              color={expert.isFavorite ? "#ef4444" : (darkMode ? "#94a3b8" : "#64748b")} 
            />
          </TouchableOpacity>
        </View>
        
        <View style={styles.expertInfo}>
          <Text style={styles.expertName}>{expert.name}</Text>
          <Text style={styles.expertTitle}>{expert.title}</Text>
          <Text style={styles.expertCompany}>{expert.company}</Text>
          
          <View style={styles.ratingContainer}>
            <View style={styles.ratingStars}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Ionicons
                  key={star}
                  name={star <= Math.floor(expert.rating) ? "star" : star <= expert.rating ? "star-half" : "star-outline"}
                  size={16}
                  color="#fbbf24"
                />
              ))}
            </View>
            <Text style={styles.ratingText}>{expert.rating}</Text>
            <Text style={styles.reviewsText}>({expert.reviews} reviews)</Text>
          </View>
        </View>
        
        <View style={styles.expertDetails}>
          <View style={styles.expertiseSection}>
            <Ionicons name="briefcase-outline" size={16} color="#94a3b8" />
            <Text style={styles.expertiseLabel}>Expertise</Text>
            <View style={styles.expertiseTags}>
              {expert.expertise.slice(0, 2).map((skill, skillIndex) => (
                <View key={`${expert.id}-skill-${skillIndex}`} style={styles.expertiseTag}>
                  <Text style={styles.expertiseTagText}>{skill}</Text>
                </View>
              ))}
              {expert.expertise.length > 2 && (
                <View style={styles.moreExpertiseTag}>
                  <Text style={styles.moreExpertiseText}>+{expert.expertise.length - 2}</Text>
                </View>
              )}
            </View>
          </View>
          
          <View style={styles.experienceSection}>
            <Ionicons name="time-outline" size={16} color="#94a3b8" />
            <Text style={styles.experienceText}>{expert.experience} experience</Text>
          </View>
        </View>
        
        <View style={styles.expertFooter}>
          <View style={styles.priceSection}>
            <Text style={styles.priceText}>£{expert.pricePerHour}</Text>
            <Text style={styles.priceLabel}>/hour</Text>
          </View>
          
          <View style={styles.actionButtons}>
            <TouchableOpacity 
              style={[
                styles.chatButton,
                !expert.isOnline && styles.chatButtonDisabled
              ]}
              onPress={() => openQuickChat(expert)}
              disabled={!expert.isOnline}
            >
              <Ionicons 
                name="chatbubble-outline" 
                size={18} 
                color={expert.isOnline ? "#ffffff" : "#94a3b8"} 
              />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[
                styles.bookButton,
                !expert.isAvailable && styles.bookButtonDisabled
              ]}
              onPress={() => openBookingModal(expert)}
              disabled={!expert.isAvailable}
            >
              <LinearGradient
                colors={expert.isAvailable ? getCategoryGradient(expert.category) : ['#94a3b8', '#cbd5e1']}
                style={styles.bookButtonGradient}
              >
                <Text style={styles.bookButtonText}>
                  {expert.isAvailable ? 'Book Now' : 'Unavailable'}
                </Text>
                <Ionicons name="arrow-forward" size={16} color="#ffffff" />
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderSessionCard = ({ item: session, index }) => (
    <TouchableOpacity
      style={styles.sessionCard}
      onPress={() => openSessionModal(session)}
      key={`session-${session.id}-${index}`}
    >
      <LinearGradient
        colors={gradients.card}
        style={styles.sessionCardInner}
      >
        <View style={styles.sessionHeader}>
          <View style={styles.sessionAvatar}>
            <Text style={styles.sessionAvatarText}>
              {session.expertName.split(' ').map(n => n[0]).join('')}
            </Text>
          </View>
          <View style={styles.sessionInfo}>
            <Text style={styles.sessionExpertName}>{session.expertName}</Text>
            <Text style={styles.sessionSpecialization}>{session.expertSpecialization}</Text>
            <View style={[
              styles.sessionStatus,
              { backgroundColor: session.status === 'confirmed' ? '#10b98120' : 
                session.status === 'completed' ? '#3b82f620' : '#f59e0b20' }
            ]}>
              <Ionicons 
                name={session.status === 'confirmed' ? "checkmark-circle" : 
                      session.status === 'completed' ? "checkmark-done-circle" : "time"} 
                size={12} 
                color={session.status === 'confirmed' ? '#10b981' : 
                      session.status === 'completed' ? '#3b82f6' : '#f59e0b'} 
              />
              <Text style={[
                styles.sessionStatusText,
                { color: session.status === 'confirmed' ? '#10b981' : 
                        session.status === 'completed' ? '#3b82f6' : '#f59e0b' }
              ]}>
                {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
              </Text>
            </View>
          </View>
          <View style={[
            styles.sessionType,
            { backgroundColor: session.type === 'video' ? '#3b82f620' : 
                          session.type === 'voice' ? '#10b98120' : '#8b5cf620' }
          ]}>
            <Ionicons 
              name={session.type === 'video' ? 'videocam' : session.type === 'voice' ? 'call' : 'chatbubble'} 
              size={20} 
              color={session.type === 'video' ? '#3b82f6' : 
                    session.type === 'voice' ? '#10b981' : '#8b5cf6'} 
            />
          </View>
        </View>
        
        <View style={styles.sessionDetails}>
          <View style={styles.sessionDateTime}>
            <Ionicons name="calendar-outline" size={16} color="#94a3b8" />
            <Text style={styles.sessionDateText}>
              {formatDate(session.date)}
            </Text>
            <Ionicons name="time-outline" size={16} color="#94a3b8" style={styles.sessionTimeIcon} />
            <Text style={styles.sessionTimeText}>{session.time} ({session.duration} min)</Text>
          </View>
          
          {session.notes && (
            <View style={styles.sessionNotesContainer}>
              <Ionicons name="document-text-outline" size={14} color="#94a3b8" />
              <Text style={styles.sessionNotes} numberOfLines={2}>
                {session.notes}
              </Text>
            </View>
          )}
          
          <View style={styles.sessionFooter}>
            <View style={styles.sessionPrice}>
              <Text style={styles.sessionPriceText}>£{session.price.toFixed(2)}</Text>
            </View>
            
            {session.status === 'confirmed' && session.meetingLink && (
              <TouchableOpacity 
                style={styles.joinButton}
                onPress={() => Linking.openURL(session.meetingLink)}
              >
                <LinearGradient
                  colors={gradients.success}
                  style={styles.joinButtonGradient}
                >
                  <Text style={styles.joinButtonText}>Join</Text>
                  <Ionicons name="arrow-forward" size={14} color="#ffffff" />
                </LinearGradient>
              </TouchableOpacity>
            )}
            
            {session.status === 'pending' && (
              <TouchableOpacity 
                style={styles.cancelButton}
                onPress={() => cancelSession(session.id)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );

  const renderBookingModal = () => (
    <Modal
      visible={bookingModalVisible}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setBookingModalVisible(false)}
    >
      <KeyboardAvoidingView 
        style={styles.modalOverlay}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={{ maxHeight: height * 0.9 }}
          contentContainerStyle={styles.modalScrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableWithoutFeedback onPress={() => Keyboard.dismiss()}>
            <View style={styles.modalContent}>
              <LinearGradient
                colors={selectedExpert ? getCategoryGradient(selectedExpert.category) : gradients.primary}
                style={styles.modalHeader}
              >
                <Text style={styles.modalTitle}>Book Session</Text>
                <TouchableOpacity 
                  onPress={() => setBookingModalVisible(false)}
                  style={styles.closeButton}
                >
                  <Ionicons name="close" size={24} color="#ffffff" />
                </TouchableOpacity>
              </LinearGradient>
              
              {selectedExpert && (
                <>
                  <View style={styles.modalExpertInfo}>
                    <LinearGradient
                      colors={[selectedExpert.avatarColor, `${selectedExpert.avatarColor}80`]}
                      style={styles.modalExpertAvatar}
                    >
                      <Text style={styles.modalExpertAvatarText}>
                        {selectedExpert.name.split(' ').map(n => n[0]).join('')}
                      </Text>
                    </LinearGradient>
                    <View style={styles.modalExpertDetails}>
                      <Text style={styles.modalExpertName}>{selectedExpert.name}</Text>
                      <Text style={styles.modalExpertTitle}>{selectedExpert.title}</Text>
                      <Text style={styles.modalExpertPrice}>£{selectedExpert.pricePerHour}/hour</Text>
                    </View>
                  </View>
                  
                  <View style={styles.bookingSection}>
                    <Text style={styles.bookingSectionTitle}>Select Duration</Text>
                    <View style={styles.durationOptions}>
                      {durationOptions.map((duration) => (
                        <TouchableOpacity
                          key={`duration-${duration}`}
                          style={[
                            styles.durationOption,
                            bookingDuration === duration && styles.durationOptionActive
                          ]}
                          onPress={() => setBookingDuration(duration)}
                        >
                          <Text style={[
                            styles.durationText,
                            bookingDuration === duration && styles.durationTextActive
                          ]}>
                            {duration} min
                          </Text>
                          <Text style={[
                            styles.durationPrice,
                            bookingDuration === duration && styles.durationPriceActive
                          ]}>
                            £{(selectedExpert.pricePerHour * duration / 60).toFixed(2)}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                  
                  <View style={styles.bookingSection}>
                    <Text style={styles.bookingSectionTitle}>Select Date & Time</Text>
                    <View style={styles.timeSelection}>
                      <TouchableOpacity 
                        style={styles.dateSelector}
                        onPress={() => setShowDatePicker(true)}
                      >
                        <Ionicons name="calendar" size={20} color={darkMode ? "#94a3b8" : "#64748b"} />
                        <Text style={styles.dateText}>
                          {formatDate(bookingDate)}
                        </Text>
                        <View style={styles.dateChangeButton}>
                          <Text style={styles.dateChangeText}>Change</Text>
                        </View>
                      </TouchableOpacity>
                      
                      <ScrollView 
                        horizontal 
                        showsHorizontalScrollIndicator={false}
                        style={styles.timeSlotContainer}
                      >
                        {timeSlots.map((slot) => (
                          <TouchableOpacity
                            key={`slot-${slot}`}
                            style={[
                              styles.timeSlot,
                              bookingTime === slot && styles.timeSlotActive
                            ]}
                            onPress={() => setBookingTime(slot)}
                          >
                            <Text style={[
                              styles.timeSlotText,
                              bookingTime === slot && styles.timeSlotTextActive
                            ]}>
                              {slot}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  </View>
                  
                  <View style={styles.bookingSection}>
                    <Text style={styles.bookingSectionTitle}>Session Notes</Text>
                    <TextInput
                      style={styles.notesInput}
                      placeholder="What would you like to discuss? Share details to help the expert prepare..."
                      value={bookingNotes}
                      onChangeText={setBookingNotes}
                      placeholderTextColor={darkMode ? "#94a3b8" : "#64748b"}
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                    />
                  </View>
                  
                  <View style={styles.bookingSummary}>
                    <Text style={styles.summaryTitle}>Booking Summary</Text>
                    <View style={styles.summaryRow}>
                      <Text style={styles.summaryLabel}>Duration</Text>
                      <Text style={styles.summaryValue}>{bookingDuration} minutes</Text>
                    </View>
                    <View style={styles.summaryRow}>
                      <Text style={styles.summaryLabel}>Date & Time</Text>
                      <Text style={styles.summaryValue}>
                        {formatDate(bookingDate)} at {bookingTime}
                      </Text>
                    </View>
                    <View style={styles.summaryRow}>
                      <Text style={styles.summaryLabel}>Expert Rate</Text>
                      <Text style={styles.summaryValue}>£{selectedExpert.pricePerHour}/hour</Text>
                    </View>
                    <View style={[styles.summaryRow, styles.totalRow]}>
                      <Text style={styles.totalLabel}>Total</Text>
                      <Text style={styles.totalValue}>
                        £{((selectedExpert.pricePerHour * bookingDuration) / 60).toFixed(2)}
                      </Text>
                    </View>
                  </View>
                  
                  <View style={styles.modalActions}>
                    <TouchableOpacity 
                      style={styles.cancelBookingButton}
                      onPress={() => setBookingModalVisible(false)}
                    >
                      <Text style={styles.cancelBookingText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={styles.confirmBookingButton}
                      onPress={bookSession}
                      disabled={isBooking}
                    >
                      <LinearGradient
                        colors={getCategoryGradient(selectedExpert.category)}
                        style={styles.confirmBookingGradient}
                      >
                        {isBooking ? (
                          <ActivityIndicator color="#ffffff" />
                        ) : (
                          <>
                            <Text style={styles.confirmBookingText}>Confirm Booking</Text>
                            <Ionicons name="lock-closed" size={16} color="#ffffff" />
                          </>
                        )}
                      </LinearGradient>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>
          </TouchableWithoutFeedback>
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderSessionModal = () => (
    <Modal
      visible={sessionModalVisible}
      animationType="slide"
      onRequestClose={() => setSessionModalVisible(false)}
    >
      <SafeAreaView style={styles.sessionModalContainer}>
        {selectedSession && (
          <>
            <LinearGradient
              colors={gradients.primary}
              style={styles.sessionModalHeader}
            >
              <TouchableOpacity 
                onPress={() => setSessionModalVisible(false)}
                style={styles.backButton}
              >
                <Ionicons name="arrow-back" size={24} color="#ffffff" />
              </TouchableOpacity>
              <Text style={styles.sessionModalTitle}>Session Details</Text>
              <View style={styles.headerSpacer} />
            </LinearGradient>
            
            <ScrollView style={styles.sessionModalContent}>
              <View style={styles.sessionDetailCard}>
                <View style={styles.detailHeader}>
                  <View style={styles.detailAvatar}>
                    <Text style={styles.detailAvatarText}>
                      {selectedSession.expertName.split(' ').map(n => n[0]).join('')}
                    </Text>
                  </View>
                  <View>
                    <Text style={styles.detailExpertName}>{selectedSession.expertName}</Text>
                    <Text style={styles.detailSpecialization}>{selectedSession.expertSpecialization}</Text>
                    <View style={[
                      styles.statusBadge,
                      { backgroundColor: selectedSession.status === 'confirmed' ? '#10b981' : 
                        selectedSession.status === 'completed' ? '#3b82f6' : '#f59e0b' }
                    ]}>
                      <Text style={styles.statusBadgeText}>
                        {selectedSession.status.charAt(0).toUpperCase() + selectedSession.status.slice(1)}
                      </Text>
                    </View>
                  </View>
                </View>
                
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>📅 Session Details</Text>
                  <View style={styles.detailItem}>
                    <Ionicons name="calendar" size={16} color={darkMode ? "#94a3b8" : "#64748b"} />
                    <Text style={styles.detailText}>{formatDate(selectedSession.date)}</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <Ionicons name="time" size={16} color={darkMode ? "#94a3b8" : "#64748b"} />
                    <Text style={styles.detailText}>{selectedSession.time} ({selectedSession.duration} minutes)</Text>
                  </View>
                  <View style={styles.detailItem}>
                    <Ionicons name="videocam" size={16} color={darkMode ? "#94a3b8" : "#64748b"} />
                    <Text style={styles.detailText}>
                      {selectedSession.type === 'video' ? 'Video Call' : 
                       selectedSession.type === 'voice' ? 'Voice Call' : 'Chat Session'}
                    </Text>
                  </View>
                  <View style={styles.detailItem}>
                    <Ionicons name="cash" size={16} color={darkMode ? "#94a3b8" : "#64748b"} />
                    <Text style={styles.detailText}>£{selectedSession.price.toFixed(2)}</Text>
                  </View>
                </View>
                
                {selectedSession.notes && (
                  <View style={styles.detailSection}>
                    <Text style={styles.detailSectionTitle}>📝 Notes</Text>
                    <Text style={styles.notesText}>{selectedSession.notes}</Text>
                  </View>
                )}
                
                {selectedSession.preparation && (
                  <View style={styles.detailSection}>
                    <Text style={styles.detailSectionTitle}>🎯 Preparation</Text>
                    {selectedSession.preparation.map((item, index) => (
                      <View key={`prep-${index}`} style={styles.preparationItem}>
                        <Ionicons name="checkmark-circle" size={16} color="#10b981" />
                        <Text style={styles.preparationText}>{item}</Text>
                      </View>
                    ))}
                  </View>
                )}
                
                {selectedSession.meetingLink && selectedSession.status === 'confirmed' && (
                  <View style={styles.detailSection}>
                    <Text style={styles.detailSectionTitle}>🔗 Meeting Link</Text>
                    <TouchableOpacity 
                      style={styles.meetingLinkButton}
                      onPress={() => Linking.openURL(selectedSession.meetingLink)}
                    >
                      <Text style={styles.meetingLinkText}>Join Session</Text>
                      <Ionicons name="open-outline" size={16} color="#ffffff" />
                    </TouchableOpacity>
                  </View>
                )}
                
                {selectedSession.status === 'completed' && selectedSession.recordingAvailable && (
                  <View style={styles.detailSection}>
                    <Text style={styles.detailSectionTitle}>🎥 Recording</Text>
                    <TouchableOpacity style={styles.recordingButton}>
                      <Ionicons name="play-circle" size={20} color="#6366f1" />
                      <Text style={styles.recordingText}>Watch Recording</Text>
                    </TouchableOpacity>
                  </View>
                )}
                
                {selectedSession.status === 'pending' && (
                  <TouchableOpacity 
                    style={styles.cancelSessionButton}
                    onPress={() => cancelSession(selectedSession.id)}
                  >
                    <Text style={styles.cancelSessionButtonText}>Cancel Session</Text>
                  </TouchableOpacity>
                )}
              </View>
            </ScrollView>
          </>
        )}
      </SafeAreaView>
    </Modal>
  );

  const renderChatModal = () => (
    <Modal
      visible={chatModalVisible}
      animationType="slide"
      onRequestClose={() => setChatModalVisible(false)}
    >
      <KeyboardAvoidingView 
        style={styles.chatModalContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {chatExpert && (
          <>
            <LinearGradient
              colors={getCategoryGradient(chatExpert.category)}
              style={styles.chatHeader}
            >
              <TouchableOpacity
                style={styles.chatCloseButton}
                onPress={() => setChatModalVisible(false)}
              >
                <Ionicons name="arrow-back" size={24} color="#ffffff" />
              </TouchableOpacity>
              <View style={styles.chatExpertInfo}>
                <View style={styles.chatExpertAvatar}>
                  <Text style={styles.chatExpertAvatarText}>
                    {chatExpert.name.split(' ').map(n => n[0]).join('')}
                  </Text>
                </View>
                <View>
                  <Text style={styles.chatExpertName}>{chatExpert.name}</Text>
                  <Text style={styles.chatExpertStatus}>
                    {chatExpert.isOnline ? '🟢 Online' : '🔴 Offline'}
                  </Text>
                </View>
              </View>
              <TouchableOpacity
                style={styles.chatMenuButton}
                onPress={() => {
                  setChatModalVisible(false);
                  setTimeout(() => openBookingModal(chatExpert), 300);
                }}
              >
                <Ionicons name="calendar-outline" size={24} color="#ffffff" />
              </TouchableOpacity>
            </LinearGradient>

            <FlatList
              ref={chatScrollViewRef}
              data={chatMessages}
              keyExtractor={(item) => item.id.toString()}
              style={styles.chatMessages}
              renderItem={({ item }) => (
                <View style={[
                  styles.messageContainer,
                  item.isExpert ? styles.expertMessageContainer : styles.userMessageContainer
                ]}>
                  {item.isExpert && (
                    <View style={styles.expertChatAvatar}>
                      <Text style={styles.expertChatAvatarText}>
                        {chatExpert.name.split(' ').map(n => n[0]).join('')}
                      </Text>
                    </View>
                  )}
                  <View style={[
                    styles.messageBubble,
                    item.isExpert ? styles.expertMessageBubble : styles.userMessageBubble
                  ]}>
                    <Text style={[
                      styles.messageText,
                      item.isExpert ? styles.expertMessageText : styles.userMessageText
                    ]}>
                      {item.text}
                    </Text>
                    <Text style={styles.messageTime}>
                      {item.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Text>
                  </View>
                </View>
              )}
              ListFooterComponent={
                isTyping && (
                  <View style={styles.typingIndicator}>
                    <Text style={styles.typingText}>{chatExpert.name.split(' ')[0]} is typing...</Text>
                  </View>
                )
              }
            />
            
            <View style={styles.chatInputContainer}>
              <TextInput
                style={styles.chatInput}
                placeholder="Type your message..."
                value={inputMessage}
                onChangeText={setInputMessage}
                placeholderTextColor={darkMode ? "#94a3b8" : "#64748b"}
                multiline
                onSubmitEditing={sendChatMessage}
              />
              <TouchableOpacity
                style={styles.sendButton}
                onPress={sendChatMessage}
                disabled={!inputMessage.trim()}
              >
                <LinearGradient
                  colors={inputMessage.trim() ? getCategoryGradient(chatExpert.category) : ['#e5e7eb', '#f3f4f6']}
                  style={styles.sendButtonGradient}
                >
                  <Ionicons 
                    name="send" 
                    size={20} 
                    color={inputMessage.trim() ? "#ffffff" : "#94a3b8"} 
                  />
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </>
        )}
      </KeyboardAvoidingView>
    </Modal>
  );

  const renderWelcomeSection = () => (
    <Animated.View 
      style={[
        styles.welcomeSection,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <LinearGradient
        colors={gradients.primary}
        style={styles.welcomeGradient}
      >
        <View style={styles.welcomeContent}>
          <View style={styles.welcomeTextContainer}>
            <Text style={styles.welcomeTitle}>👨‍💼 Expert Guidance</Text>
            <Text style={styles.welcomeSubtitle}>
              Connect with {experts.length}+ industry professionals for personalized mentorship
            </Text>
          </View>
          <View style={styles.welcomeStats}>
            <View style={styles.statItem}>
              <Ionicons name="people" size={20} color="#ffffff" />
              <Text style={styles.statNumber}>{experts.filter(e => e.isOnline).length}</Text>
              <Text style={styles.statLabel}>Online</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Ionicons name="star" size={20} color="#ffffff" />
              <Text style={styles.statNumber}>4.8</Text>
              <Text style={styles.statLabel}>Rating</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Ionicons name="time" size={20} color="#ffffff" />
              <Text style={styles.statNumber}>24/7</Text>
              <Text style={styles.statLabel}>Support</Text>
            </View>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderFilterSection = () => (
    <View style={styles.filterSection}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <TouchableOpacity
          style={[
            styles.filterButton,
            priceFilter === 'all' && styles.filterButtonActive
          ]}
          onPress={() => setPriceFilter('all')}
        >
          <Text style={[
            styles.filterText,
            priceFilter === 'all' && styles.filterTextActive
          ]}>All Prices</Text>
        </TouchableOpacity>
        
        {['0-50', '50-100', '100+'].map((range) => (
          <TouchableOpacity
            key={`price-${range}`}
            style={[
              styles.filterButton,
              priceFilter === range && styles.filterButtonActive
            ]}
            onPress={() => setPriceFilter(range)}
          >
            <Text style={[
              styles.filterText,
              priceFilter === range && styles.filterTextActive
            ]}>
              {range === '100+' ? 'Over £100' : `£${range}`}
            </Text>
          </TouchableOpacity>
        ))}
        
        <TouchableOpacity
          style={[
            styles.filterButton,
            availabilityFilter === 'now' && styles.filterButtonActive
          ]}
          onPress={() => setAvailabilityFilter('now')}
        >
          <Ionicons 
            name={availabilityFilter === 'now' ? "flash" : "flash-outline"} 
            size={16} 
            color={availabilityFilter === 'now' ? "#ffffff" : "#94a3b8"} 
          />
          <Text style={[
            styles.filterText,
            availabilityFilter === 'now' && styles.filterTextActive
          ]}>Available Now</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[
            styles.filterButton,
          ]}
          onPress={() => setShowSortModal(true)}
        >
          <Ionicons 
            name="filter" 
            size={16} 
            color="#94a3b8" 
          />
          <Text style={styles.filterText}>Sort</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );

  const renderSortModal = () => (
    <Modal
      visible={showSortModal}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowSortModal(false)}
    >
      <TouchableWithoutFeedback onPress={() => setShowSortModal(false)}>
        <View style={styles.sortModalOverlay}>
          <TouchableWithoutFeedback>
            <View style={styles.sortModalContent}>
              <Text style={styles.sortModalTitle}>Sort By</Text>
              {[
                { id: 'rating', label: 'Highest Rating' },
                { id: 'price_low', label: 'Price: Low to High' },
                { id: 'price_high', label: 'Price: High to Low' },
                { id: 'experience', label: 'Most Experienced' },
                { id: 'popularity', label: 'Most Popular' }
              ].map((option) => (
                <TouchableOpacity
                  key={`sort-${option.id}`}
                  style={styles.sortOption}
                  onPress={() => {
                    setSortBy(option.id);
                    setShowSortModal(false);
                  }}
                >
                  <Text style={[
                    styles.sortOptionText,
                    sortBy === option.id && styles.sortOptionTextActive
                  ]}>
                    {option.label}
                  </Text>
                  {sortBy === option.id && (
                    <Ionicons name="checkmark" size={20} color="#6366f1" />
                  )}
                </TouchableOpacity>
              ))}
              <TouchableOpacity 
                style={styles.closeSortButton}
                onPress={() => setShowSortModal(false)}
              >
                <Text style={styles.closeSortText}>Close</Text>
              </TouchableOpacity>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );

  if (loading) {
    return renderLoading();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#6366f1" />
      
      {renderHeader()}
      {renderTabNavigation()}
      
      <Animated.FlatList
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#6366f1']}
            tintColor="#6366f1"
          />
        }
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: true }
        )}
        scrollEventThrottle={16}
        ListHeaderComponent={
          <>
            {renderWelcomeSection()}
            {renderFilterSection()}
          </>
        }
        data={
          selectedTab === 'available' ? filteredExperts :
          selectedTab === 'upcoming' ? upcomingSessions :
          selectedTab === 'past' ? pastSessions :
          experts.filter(e => e.isFavorite)
        }
        keyExtractor={(item) => item.id}
        renderItem={
          selectedTab === 'available' || selectedTab === 'favorites' ? 
          renderExpertCard : renderSessionCard
        }
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Ionicons 
              name={
                selectedTab === 'available' ? "search-outline" :
                selectedTab === 'upcoming' ? "calendar-outline" :
                selectedTab === 'past' ? "time-outline" : "star-outline"
              } 
              size={64} 
              color="#94a3b8" 
            />
            <Text style={styles.emptyStateTitle}>
              {selectedTab === 'available' ? 'No experts available' :
               selectedTab === 'upcoming' ? 'No upcoming sessions' :
               selectedTab === 'past' ? 'No past sessions' : 'No favorite experts'}
            </Text>
            <Text style={styles.emptyStateSubtitle}>
              {selectedTab === 'available' ? 'Try adjusting your filters or check back later' :
               selectedTab === 'upcoming' ? 'Book your first session with an expert' :
               selectedTab === 'past' ? 'Your completed sessions will appear here' : 
               'Tap the heart icon to save experts for later'}
            </Text>
            {selectedTab === 'upcoming' && (
              <TouchableOpacity 
                style={styles.emptyStateButton}
                onPress={() => setSelectedTab('available')}
              >
                <Text style={styles.emptyStateButtonText}>Browse Experts</Text>
              </TouchableOpacity>
            )}
          </View>
        }
      />

      {renderBookingModal()}
      {renderSessionModal()}
      {renderChatModal()}
      {renderSortModal()}
      
      {showDatePicker && Platform.OS === 'ios' && (
        <DateTimePicker
          value={bookingDate}
          mode="date"
          display="spinner"
          onChange={(event, date) => {
            setShowDatePicker(false);
            if (date) setBookingDate(date);
          }}
        />
      )}
      
      <TouchableOpacity 
        style={styles.fab}
        onPress={() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }}
      >
        <LinearGradient
          colors={gradients.primary}
          style={styles.fabGradient}
        >
          <Ionicons name="search" size={24} color="#ffffff" />
        </LinearGradient>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

export default AskExpert;